import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { Lead, Contact } from '@/types'

// Route context info
export interface RouteContext {
  path: string
  pageName: string
  section?: string
}

// Selected item context
export interface SelectedItemContext {
  type: 'lead' | 'contact' | 'conversation' | 'content'
  id: string
  name: string
  data?: Lead | Contact | Record<string, unknown>
}

// Full context state
interface ChatContextState {
  route: RouteContext | null
  selectedItem: SelectedItemContext | null
  isContextEnabled: boolean
}

// Chat context actions
interface ChatContextActions {
  setRouteContext: (route: RouteContext | null) => void
  setSelectedItem: (item: SelectedItemContext | null) => void
  clearContext: () => void
  setContextEnabled: (enabled: boolean) => void
}

type ChatContextStore = ChatContextState & ChatContextActions

const initialState: ChatContextState = {
  route: null,
  selectedItem: null,
  isContextEnabled: true,
}

export const useChatContextStore = create<ChatContextStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setRouteContext: (route: RouteContext | null) => {
        set({ route })
      },

      setSelectedItem: (item: SelectedItemContext | null) => {
        set({ selectedItem: item })
      },

      clearContext: () => {
        set({ selectedItem: null })
      },

      setContextEnabled: (enabled: boolean) => {
        set({ isContextEnabled: enabled })
      },
    }),
    { name: 'ChatContextStore', enabled: import.meta.env.DEV }
  )
)

// Selectors
export const selectRouteContext = (state: ChatContextStore) => state.route
export const selectSelectedItem = (state: ChatContextStore) => state.selectedItem
export const selectIsContextEnabled = (state: ChatContextStore) => state.isContextEnabled
export const selectHasContext = (state: ChatContextStore) =>
  state.route !== null || state.selectedItem !== null

// Helper to format context for AI
export const formatContextForAI = (state: ChatContextStore): string => {
  if (!state.isContextEnabled) return ''

  const parts: string[] = []

  if (state.route) {
    parts.push(`Current page: ${state.route.pageName}`)
    if (state.route.section) {
      parts.push(`Section: ${state.route.section}`)
    }
  }

  if (state.selectedItem) {
    parts.push(`Selected ${state.selectedItem.type}: ${state.selectedItem.name}`)
    if (state.selectedItem.data) {
      // Add relevant item details
      const data = state.selectedItem.data
      if (state.selectedItem.type === 'lead' && 'status' in data && 'score' in data) {
        const lead = data as Lead
        parts.push(`  - Status: ${lead.status}`)
        parts.push(`  - Score: ${lead.score}`)
        if (lead.email) parts.push(`  - Email: ${lead.email}`)
        if (lead.company) parts.push(`  - Company: ${lead.company}`)
        if (lead.source) parts.push(`  - Source: ${lead.source}`)
      }
    }
  }

  return parts.length > 0 ? `[Context]\n${parts.join('\n')}` : ''
}
